import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  startTransition,
} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { TopBar } from "@/components/webbuilder/layout/TopBar";
import { Sidebar } from "@/components/webbuilder/layout/Sidebar";
import { Canvas } from "@/components/webbuilder/features/canvas/Canvas";
import { usePages } from "@/hooks/usePages";
import { useWebBuilderPreloader } from "@/hooks/useWebBuilderPreloader";
import { useWebBuilderResolution } from "@/hooks/useWebBuilderResolution";
import { Section } from "@/types/models/page";
import { WebBuilderPreloader } from "@/components/webbuilder/ui/loaders/WebBuilderPreloader";
import { ResolutionWarningOverlay } from "@/components/webbuilder/ui/overlays/ResolutionWarningOverlay";
import { useTheme } from "@/contexts/ThemeContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ButtonPrimary } from "@/components/common/ButtonPrimary";
import { ButtonOutline } from "@/components/common/ButtonOutline";
import { AlertTriangle } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  setSidebarCollapsed,
  setShowSectionPropertiesPanel,
  setCurrentDevice,
} from "@/store/slices/ui.slice";
import {
  setSelectedSection,
  setSections,
} from "@/store/slices/webbuilderSlice";
import { selectSectionsArray } from "@/store/selectors/webbuilder.selectors";
import { createDraftRequest, draftFormReset } from "@/store/slices/page.slice";
import { store } from "@/store/store";
import { toast } from "sonner";
import { DebugJsonPanel } from "@/components/webbuilder/features/sections/DebugJsonPanel";
import { SectionPropertiesPanel } from "@/components/webbuilder/features/sections/SectionPropertiesPanel";
import { KeyboardShortcutsPanel } from "@/components/webbuilder/ui/panels/KeyboardShortcutsPanel";
import { sectionDataService } from "@/services/sectionDataService";
import { useUndoRedo } from "@/hooks/useUndoRedo";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { ComponentDefinition } from "@/types/models/componentConfig";
import { componentService } from "@/services/componentService";
import { putPage } from "@/store/actions/page.actions";
import type { Page } from "@/types/models/page";
import { WebBuilderAddPageModal } from "@/components/webbuilder/modals/WebBuilderAddPageModal";

export default function WebBuilderEditPage() {
  const { storeType, storeId } = useParams<{
    storeType: string;
    storeId: string;
  }>();
  const [searchParams] = useSearchParams();
  const [componentDefinitions, setComponentDefinitions] = useState<
    ComponentDefinition[]
  >([]);
  /** True after component catalog fetch settles (even if empty — blank pages have no sections to infer defs). */
  const [componentDefinitionsReady, setComponentDefinitionsReady] =
    useState(false);
  const dispatch = useAppDispatch();
  const { isDark, toggleTheme, themeColors, isThemeLoading } = useTheme();
  const {
    sidebarCollapsed,
    showSectionPropertiesPanel,
    currentDevice,
    showFullscreenEditor,
  } = useAppSelector((state) => state.ui as any);

  console.log("🔴 edit.tsx currentDevice from Redux:", currentDevice);
  // Move these selectors to the top level to avoid conditional hook calls
  // Read selectedSectionId from store (safe) and try to resolve the section
  const selectedSectionIdFromStore = useAppSelector((state) => {
    const wb = (state as any).webbuilder;
    return wb ? (wb.selectedSectionId ?? null) : null;
  });

  const selectedSectionFromStore = useAppSelector((state) => {
    const wb = (state as any).webbuilder;
    if (!wb) return null;
    const id = wb.selectedSectionId;
    return id ? (wb.sectionsById?.[id] ?? null) : null;
  });

  // Get sections from Redux store for publish/save operations
  const sectionsFromRedux =
    (useAppSelector(selectSectionsArray) as any[]) || [];

  const pageDrafts = useAppSelector(
    (state) => (state && (state as any).page?.drafts) || [],
  ) as Array<{ id: string }>;

  // Use pages hook
  const {
    pages,
    loading: pagesLoading,
    error: pagesError,
    form,
    currentPage,
    currentPageLoading,
    currentPageError,
    actions,
  } = usePages();

  // Use Redux sections if available, otherwise fall back to currentPage.sections
  const sections =
    sectionsFromRedux.length > 0
      ? sectionsFromRedux
      : currentPage?.sections || [];

  // Fallback: resolve selected section from store first, then fall back to
  // `currentPage.sections`. Memoize the result so child components receive a
  // stable reference and avoid unnecessary re-renders when unrelated state
  // changes occur.
  const selectedSection = useMemo(() => {
    if (selectedSectionFromStore) return selectedSectionFromStore;
    if (selectedSectionIdFromStore && currentPage?.sections) {
      return (
        currentPage.sections.find(
          (s: any) => s.id === selectedSectionIdFromStore,
        ) || null
      );
    }
    return null;
  }, [
    selectedSectionFromStore,
    selectedSectionIdFromStore,
    currentPage?.sections,
  ]);
  const selectedSectionHistory = selectedSection
    ? sectionDataService.getChangeHistory(selectedSection.id)
    : [];

  const [activeTab, setActiveTab] = useState("Sections");
  const [availableWidth, setAvailableWidth] = useState(1200);
  const [isCanvasShimmering, setIsCanvasShimmering] = useState(false);
  const [selectedPageId, setSelectedPageId] = useState<string>("");
  const [isPreloaderExiting, setIsPreloaderExiting] = useState(false);
  const [showPreloader, setShowPreloader] = useState(true);
  const [showMainContent, setShowMainContent] = useState(false);
  const [showCanvas, setShowCanvas] = useState(false);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(
    null,
  );
  const [isDebugPanelOpen, setIsDebugPanelOpen] = useState(false);
  const [isKeyboardShortcutsPanelOpen, setIsKeyboardShortcutsPanelOpen] =
    useState(false);
  const [addPageModalOpen, setAddPageModalOpen] = useState(false);

  // Track initial sections and page status when page loads
  const [initialSections, setInitialSections] = useState<any[]>([]);
  const [initialPageStatus, setInitialPageStatus] = useState<string>("");

  // Track if we've already attempted to extract component definitions
  const hasExtractedComponentDefs = useRef(false);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [pageToDelete, setPageToDelete] = useState<string>("");
  const [confirmationInput, setConfirmationInput] = useState<string>("");
  const containerRef = useRef<HTMLDivElement>(null);
  const previousSidebarCollapsedRef = useRef<boolean | null>(null);
  const previousDeviceRef = useRef<"desktop" | "tablet" | "mobile" | null>(
    null,
  );

  // Initialize undo/redo functionality
  const { undo, redo, canUndo, canRedo } = useUndoRedo();

  // Handle save draft
  const handleSaveAsDraft = useCallback(() => {
    if (!selectedPageId || !currentPage) {
      return;
    }

    console.log("💾 Saving page as draft (via keyboard):", selectedPageId);
    actions.createDraft(selectedPageId);
  }, [selectedPageId, currentPage, actions]);

  // Handle publish
  const handlePublish = useCallback(() => {
    if (!selectedPageId || !currentPage) {
      return;
    }

    if (sections.length === 0) {
      return;
    }

    const updatePayload = {
      sections: sections.map((section: Section) => ({
        ...section,
        page_id: selectedPageId,
        updated_at: new Date().toISOString(),
      })),
      status: "published",
    };

    console.log("🚀 Publishing page (via keyboard):", selectedPageId);
    actions.updatePage(selectedPageId, updatePayload);

    // Update initial sections and status to reflect the published state
    setInitialSections(JSON.parse(JSON.stringify(sections)));
    setInitialPageStatus("published");
  }, [selectedPageId, currentPage, sections, actions]);

  const handlePublishAll = useCallback(async () => {
    if (!pages || pages.length === 0) {
      toast.error("No pages available to publish");
      return;
    }
    try {
      await Promise.all(
        pages.map((page: Page) =>
          putPage({
            pageId: page.id,
            data: { status: "published" },
          }),
        ),
      );
      toast.success("All pages published successfully");
      actions.fetchPages(1, 20);
    } catch {
      toast.error("Failed to publish all pages");
    }
  }, [pages, actions]);

  // Handle keyboard shortcuts panel
  const handleShowKeyboardShortcuts = useCallback(() => {
    setIsKeyboardShortcutsPanelOpen(true);
  }, []);

  // Memoize keyboard shortcut handlers
  const handleToggleSidebar = useCallback(() => {
    startTransition(() => {
      dispatch(setSidebarCollapsed(!sidebarCollapsed));
    });
  }, [dispatch, sidebarCollapsed]);

  const handleDeviceDesktop = useCallback(() => {
    startTransition(() => {
      dispatch(setCurrentDevice("desktop"));
    });
  }, [dispatch]);

  const handleDeviceTablet = useCallback(() => {
    startTransition(() => {
      dispatch(setCurrentDevice("tablet"));
    });
  }, [dispatch]);

  const handleDeviceMobile = useCallback(() => {
    startTransition(() => {
      dispatch(setCurrentDevice("mobile"));
    });
  }, [dispatch]);

  // Set up keyboard shortcuts
  useKeyboardShortcuts({
    onUndo: undo,
    onRedo: redo,
    canUndo,
    canRedo,
    onToggleSidebar: handleToggleSidebar,
    onToggleTheme: toggleTheme,
    onPreview: handlePreview,
    onSave: handleSaveAsDraft,
    onPublish: handlePublish,
    onDeviceDesktop: handleDeviceDesktop,
    onDeviceTablet: handleDeviceTablet,
    onDeviceMobile: handleDeviceMobile,
    onShowKeyboardShortcuts: handleShowKeyboardShortcuts,
  });

  const editorBootReady = useMemo(() => {
    if (pagesLoading || isThemeLoading || !componentDefinitionsReady) {
      return false;
    }
    if (pagesError) {
      return true;
    }
    if (pages.length === 0) {
      return false;
    }
    if (!selectedPageId) {
      return false;
    }
    if (currentPageLoading) {
      return false;
    }
    if (currentPageError) {
      return true;
    }
    return currentPage != null;
  }, [
    pagesLoading,
    isThemeLoading,
    componentDefinitionsReady,
    pagesError,
    pages.length,
    selectedPageId,
    currentPageLoading,
    currentPageError,
    currentPage,
  ]);

  // Use preloader hook with proper dependency handling
  const { isPreloaderVisible, progress, currentStep } = useWebBuilderPreloader({
    minLoadingTime: 2000,
    dependencies: [editorBootReady],
    loadingSteps: [
      "Initializing Web Builder...",
      "Loading your pages...",
      "Loading theme...",
      "Loading component definitions...",
      "Setting up the canvas...",
      "Preparing components...",
      "Almost ready...",
    ],
  });

  // Fetch component definitions - only once on mount
  useEffect(() => {
    const storeIdParam = storeId as string;
    if (!storeIdParam) {
      setComponentDefinitionsReady(true);
      return;
    }

    let cancelled = false;

    componentService
      .getComponents(storeIdParam)
      .then((defs) => {
        if (cancelled) return;
        console.log("📦 Loaded component definitions:", defs.length);
        if (defs.length > 0) {
          setComponentDefinitions(defs);
        }
      })
      .catch((error) => {
        console.error("Failed to load component definitions:", error);
      })
      .finally(() => {
        if (!cancelled) {
          setComponentDefinitionsReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [storeId]); // Only run when storeId changes

  // Fallback: Extract component definitions from current page sections - only once when page loads
  useEffect(() => {
    if (
      !hasExtractedComponentDefs.current &&
      componentDefinitions.length === 0 &&
      currentPage?.sections &&
      currentPage.sections.length > 0
    ) {
      console.log("📦 Extracting component definitions from sections...");
      const extracted =
        componentService.extractComponentDefinitionsFromSections(
          currentPage.sections,
        );
      if (extracted.length > 0) {
        setComponentDefinitions(extracted);
        hasExtractedComponentDefs.current = true;
      }
    }
  }, [currentPage?.id, componentDefinitions.length]); // Only run when current page ID changes or definitions length changes

  // Handle preloader exit animation
  useEffect(() => {
    if (!isPreloaderVisible && showPreloader && !isPreloaderExiting) {
      setIsPreloaderExiting(true);
    }
  }, [
    isPreloaderVisible,
    showPreloader,
    isPreloaderExiting,
    pagesLoading,
    isThemeLoading,
    themeColors,
  ]);

  // Fallback: If preloader is exiting for too long, force show content
  useEffect(() => {
    if (isPreloaderExiting) {
      const fallbackTimer = setTimeout(() => {
        setShowPreloader(false);
        setShowMainContent(true);
        setShowCanvas(true);
      }, 800); // 800ms fallback (100ms buffer after 700ms animation)

      return () => clearTimeout(fallbackTimer);
    }
  }, [isPreloaderExiting]);

  // Debug state changes
  useEffect(() => {
    // debug effect removed: state change logging
  }, [
    showPreloader,
    showMainContent,
    showCanvas,
    isPreloaderExiting,
    isPreloaderVisible,
  ]);

  // Use resolution hook
  const { showWarning, override, currentWidth, minWidth } =
    useWebBuilderResolution();

  useEffect(() => {
    if (showFullscreenEditor) {
      if (!showSectionPropertiesPanel) {
        dispatch(setShowSectionPropertiesPanel(true));
      }

      if (!sidebarCollapsed) {
        previousSidebarCollapsedRef.current = false;
        dispatch(setSidebarCollapsed(true));
      }

      if (currentDevice !== "mobile") {
        if (!previousDeviceRef.current) {
          previousDeviceRef.current = currentDevice;
        }
        dispatch(setCurrentDevice("mobile"));
      }
    } else {
      if (previousSidebarCollapsedRef.current !== null) {
        dispatch(setSidebarCollapsed(previousSidebarCollapsedRef.current));
        previousSidebarCollapsedRef.current = null;
      }

      if (previousDeviceRef.current) {
        const previousDevice = previousDeviceRef.current;
        previousDeviceRef.current = null;
        if (currentDevice !== previousDevice) {
          dispatch(setCurrentDevice(previousDevice));
        }
      }
    }
  }, [
    showFullscreenEditor,
    sidebarCollapsed,
    showSectionPropertiesPanel,
    currentDevice,
    dispatch,
  ]);

  // Fetch pages on component mount
  useEffect(() => {
    actions.fetchPages(1, 20); // Fetch first 20 pages
  }, []); // Only run on mount

  // Note: Removed redirect logic - the main middleware handles routing
  // This page should only render if we have pages

  // Select initial page when the list loads (prefer ?pageId= when present)
  useEffect(() => {
    if (pages.length > 0 && !selectedPageId) {
      const fromQuery = searchParams.get("pageId");
      const matched = fromQuery
        ? pages.find((p: { id: string }) => p.id === fromQuery)
        : undefined;
      const initialId = matched?.id ?? pages[0].id;
      startTransition(() => {
        setSelectedPageId(initialId);
      });
    }
  }, [pages, selectedPageId, searchParams]);

  // Fetch individual page when selectedPageId changes
  useEffect(() => {
    if (selectedPageId) {
      actions.fetchPage(selectedPageId);
    }
  }, [selectedPageId]); // Remove actions from deps

  // Initialize sections in Redux store when current page loads
  useEffect(() => {
    if (currentPage && currentPage.sections) {
      console.log("🔍 Loading sections into Redux store:", {
        pageId: currentPage.id,
        pageName: currentPage.name,
        sectionsCount: currentPage.sections.length,
        sections: currentPage.sections,
      });

      // Log each section's component data structure
      currentPage.sections.forEach((section: any, index: number) => {
        console.log(`📋 Section ${index + 1} (${section.id}):`, {
          sectionId: section.id,
          sectionIdentifier: section.section_identifier,
          componentDataKeys: Object.keys(section.component_data || {}),
          componentDataStructure: section.component_data,
        });
      });

      dispatch(setSections(currentPage.sections));

      // Track initial sections and page status for change detection
      setInitialSections(JSON.parse(JSON.stringify(currentPage.sections)));
      setInitialPageStatus(currentPage.status || "");
    }
  }, [currentPage, dispatch]);

  // Listen for successful page creation from template
  useEffect(() => {
    if (form.success && !form.loading) {
      // Refresh the pages list
      actions.fetchPages(1, 20);

      // Reset the form state
      actions.resetForm();
    }
  }, [form.success, form.loading]); // Remove actions from deps

  // Detect if there are unpublished changes
  const hasUnpublishedChanges = useMemo(() => {
    if (initialSections.length === 0 || sections.length === 0) {
      return false;
    }

    // Compare current sections with initial sections
    const currentSectionsJson = JSON.stringify(sections);
    const initialSectionsJson = JSON.stringify(initialSections);

    return currentSectionsJson !== initialSectionsJson;
  }, [sections, initialSections]);

  // Handle preview
  function handlePreview() {
    if (!selectedPageId || !currentPage) {
      toast.error("No page selected for preview");
      return;
    }

    const openPreviewTab = (draftId: string) => {
      const previewUrl = `${window.location.origin}/${storeType}/${storeId}/webbuilder/preview?draftId=${encodeURIComponent(draftId)}`;
      window.open(previewUrl, "_blank", "noopener,noreferrer");
    };

    const latestDraftId = pageDrafts[pageDrafts.length - 1]?.id;

    const isDirty =
      initialSections.length > 0 &&
      sections.length > 0 &&
      JSON.stringify(sections) !== JSON.stringify(initialSections);

    if (isDirty || !latestDraftId) {
      if (sections.length === 0) {
        toast.error("No sections to save");
        return;
      }

      const unsubscribe = store.subscribe(() => {
        const state = store.getState();
        const draftForm = (state as any)?.page?.draftForm;
        if (!draftForm) {
          return;
        }

        if (draftForm.success) {
          const drafts = ((state as any)?.page?.drafts || []) as Array<{
            id: string;
          }>;
          const draftId = drafts[drafts.length - 1]?.id;
          if (draftId) {
            openPreviewTab(draftId);
          }
          store.dispatch(draftFormReset());
          unsubscribe();
        } else if (draftForm.error) {
          toast.error("Failed to save draft before preview");
          store.dispatch(draftFormReset());
          unsubscribe();
        }
      });

      store.dispatch(createDraftRequest({ pageId: selectedPageId }));
      return;
    }

    openPreviewTab(latestDraftId);
  }

  // Check if page is published
  const isPagePublished = useMemo(() => {
    return initialPageStatus === "published";
  }, [initialPageStatus]);

  // Handle page selection with unpublished changes check
  const handlePageChange = (pageId: string) => {
    // Check if there are unpublished changes
    if (hasUnpublishedChanges) {
      const confirmMessage =
        "You have unpublished changes. If you switch pages, you will lose these changes. Do you want to continue?";
      if (!window.confirm(confirmMessage)) {
        return; // Don't change page if user cancels
      }
    }

    startTransition(() => {
      setSelectedPageId(pageId);
      // Close section panel when page changes
      handleCloseSectionPanel();
    });
    console.log("Selected page:", pageId);
  };

  // Calculate available width dynamically
  const calculateAvailableWidth = useCallback(() => {
    console.log(
      "calculateAvailableWidth called, sidebarCollapsed:",
      sidebarCollapsed,
      "showSectionPropertiesPanel:",
      showSectionPropertiesPanel,
    );
    if (typeof window !== "undefined") {
      const padding = 32; // 16px on each side (px-4 = 1rem = 16px)

      // Account for sidebar width manually since the container doesn't actually resize
      const leftSidebarWidth = sidebarCollapsed ? 0 : 256; // 256px = w-64 (16rem)
      let rightPanelWidth = 0;
      if (showFullscreenEditor) {
        // when fullscreen editor is active, the preview becomes a small column on the right
        rightPanelWidth = 420; // preview width
      } else {
        rightPanelWidth = showSectionPropertiesPanel ? 256 : 0; // 256px = w-64 (16rem)
      }
      const availableContainerWidth =
        window.innerWidth - leftSidebarWidth - rightPanelWidth;
      const calculatedWidth = Math.max(
        320,
        Math.round(availableContainerWidth - padding),
      ); // Minimum 320px

      setAvailableWidth(calculatedWidth);
    }
  }, [sidebarCollapsed, showSectionPropertiesPanel, showFullscreenEditor]);

  // Effect for responsive width calculation
  useEffect(() => {
    calculateAvailableWidth();

    // Handle window resize (this will be the primary trigger since we're using window.innerWidth)
    const handleResize = () => {
      calculateAvailableWidth();
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []); // Empty dependency array - only run on mount

  // Immediate effect for sidebar toggle
  useEffect(() => {
    // Calculate immediately when sidebar state changes since we're using the state directly
    calculateAvailableWidth();
  }, [sidebarCollapsed]); // Remove calculateAvailableWidth from deps

  // Immediate effect for right panel toggle
  useEffect(() => {
    // Calculate immediately when right panel state changes
    calculateAvailableWidth();
  }, [showSectionPropertiesPanel, showFullscreenEditor]); // Remove calculateAvailableWidth from deps

  // Effect to handle page deletion and auto-select another page
  useEffect(() => {
    // If the currently selected page was deleted and there are other pages available
    if (
      selectedPageId &&
      !pages.find((p: any) => p.id === selectedPageId) &&
      pages.length > 0
    ) {
      // Select the first available page
      startTransition(() => {
        setSelectedPageId(pages[0].id);
      });
    }
    // If no pages are available, clear the selection
    else if (pages.length === 0 && selectedPageId) {
      startTransition(() => {
        setSelectedPageId("");
      });
    }
  }, [pages, selectedPageId]);

  // Effect to handle responsive sidebar behavior on window resize
  useEffect(() => {
    const handleResponsiveBehavior = () => {
      if (typeof window === "undefined") return;
      if (showFullscreenEditor) return;
      const width = window.innerWidth;
      startTransition(() => {
        if (width < 1750) {
          // On smaller screens, ensure only one sidebar is open at a time
          if (!sidebarCollapsed && showSectionPropertiesPanel) {
            dispatch(setShowSectionPropertiesPanel(false));
            setSelectedSectionId(null);
            dispatch(setSelectedSection(null));
          }
        } else {
          // On larger screens (>= 1750px), automatically open both sidebars
          const changes = [];
          if (sidebarCollapsed) {
            changes.push("left sidebar");
            dispatch(setSidebarCollapsed(false));
          }
          if (!showSectionPropertiesPanel) {
            changes.push("right panel");
            dispatch(setShowSectionPropertiesPanel(true));
          }
        }
      });
    };

    // Run on mount and window resize
    handleResponsiveBehavior();
    window.addEventListener("resize", handleResponsiveBehavior);
    return () => window.removeEventListener("resize", handleResponsiveBehavior);
  }, [
    sidebarCollapsed,
    showSectionPropertiesPanel,
    showFullscreenEditor,
    dispatch,
  ]); // Include dependencies to ensure proper updates

  // Initial setup effect - run once on mount
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (showFullscreenEditor) return;
    const width = window.innerWidth;

    startTransition(() => {
      if (width >= 1750) {
        // On larger screens, ensure both sidebars are open
        dispatch(setSidebarCollapsed(false));
        dispatch(setShowSectionPropertiesPanel(true));
      } else {
        // On smaller screens, ensure only left sidebar is open initially
        dispatch(setSidebarCollapsed(false));
        dispatch(setShowSectionPropertiesPanel(false));
      }
    });
  }, [dispatch, showFullscreenEditor]); // Include dispatch in dependency array

  // Canvas shimmer effect
  const triggerCanvasShimmer = () => {
    setIsCanvasShimmering(true);
    setTimeout(() => setIsCanvasShimmering(false), 1000);
  };

  // Device-specific width calculation
  const getDeviceWidth = () => {
    // Ensure we have a minimum available width to prevent canvas from becoming too small
    const minAvailableWidth = Math.max(availableWidth, 320);

    let deviceWidth;
    switch (currentDevice) {
      case "mobile":
        deviceWidth = Math.min(375, minAvailableWidth);
        break;
      case "tablet":
        deviceWidth = Math.min(768, minAvailableWidth);
        break;
      case "desktop":
      default:
        deviceWidth = Math.min(1920, minAvailableWidth);
        break;
    }

    return deviceWidth;
  };

  const deviceWidth = getDeviceWidth();

  // Removed unused functions createPagesFromTemplate and createPageFromTemplate
  // If needed in the future, they can be re-added
  // Canvas callback functions
  const handleRetryFetch = useCallback(() => {
    actions.fetchPages(1, 20);
  }, [actions]);

  const handleCreatePage = useCallback(() => {
    window.location.href = `/${storeType}/${storeId}/webbuilder/new-page`;
  }, [storeType, storeId]);

  const handleCloseSectionPanel = useCallback(() => {
    startTransition(() => {
      dispatch(setShowSectionPropertiesPanel(false));
      setSelectedSectionId(null);
      dispatch(setSelectedSection(null));
    });
    // Optionally expand left sidebar back when closing right panel
    // setSidebarCollapsed(false);
  }, [dispatch]);

  // Handle page deletion
  const handleDeletePage = useCallback(async (pageId: string) => {
    // Always open confirmation dialog - no restriction on last page
    setPageToDelete(pageId);
    setConfirmationInput("");
    setShowDeleteDialog(true);
  }, []);

  // Confirm page deletion
  const confirmDeletePage = useCallback(async () => {
    if (!pageToDelete) return;

    // Find the page to get its name for validation
    const pageToDeleteObj = pages.find((p: any) => p.id === pageToDelete);
    if (!pageToDeleteObj) return;

    // Check if the user typed the correct page name
    if (confirmationInput.trim() !== pageToDeleteObj.name.trim()) {
      // You could add error state here if needed
      return;
    }

    try {
      // Show a loading indicator by dispatching the action
      actions.deletePage(pageToDelete);

      // If we're deleting the currently selected page, clear selection
      if (pageToDelete === selectedPageId) {
        setSelectedPageId("");
      }

      // Close section panel
      handleCloseSectionPanel();

      // Close dialog and reset state
      setShowDeleteDialog(false);
      setPageToDelete("");
      setConfirmationInput("");

      // If this was the last page, redirect to import
      if (pages.length <= 1) {
        // Small delay to allow the deletion to process
        setTimeout(() => {
          window.location.href = `/${storeType}/${storeId}/webbuilder/import`;
        }, 500);
      }
    } catch (error) {
      console.error("Failed to delete page:", error);
      alert("Failed to delete page. Please try again.");
    }
  }, [
    actions,
    selectedPageId,
    handleCloseSectionPanel,
    pageToDelete,
    confirmationInput,
    pages,
    storeType,
    storeId,
  ]);

  // Cancel page deletion
  const cancelDeletePage = useCallback(() => {
    setShowDeleteDialog(false);
    setPageToDelete("");
    setConfirmationInput("");
  }, []);
  // Handle left sidebar toggle - ensure single sidebar rule on smaller screens
  const handleLeftSidebarToggle = useCallback(
    (collapsed: boolean) => {
      if (typeof window === "undefined") return;
      const width = window.innerWidth;
      startTransition(() => {
        if (width < 1750) {
          // On smaller screens, ensure only one sidebar is open
          if (!collapsed && showSectionPropertiesPanel) {
            // If expanding left sidebar and right panel is open, close the right panel
            dispatch(setShowSectionPropertiesPanel(false));
            setSelectedSectionId(null);
            dispatch(setSelectedSection(null));
          }
        }

        dispatch(setSidebarCollapsed(collapsed));
      });
    },
    [showSectionPropertiesPanel, dispatch],
  );

  // Handle right panel toggle - ensure single sidebar rule on smaller screens
  const handleRightPanelToggle = useCallback(() => {
    if (typeof window === "undefined") return;
    const width = window.innerWidth;

    const newState = !showSectionPropertiesPanel;

    startTransition(() => {
      if (width < 1750) {
        // On smaller screens, ensure only one sidebar is open
        if (newState && !sidebarCollapsed) {
          // If opening right panel and left sidebar is open, close the left sidebar
          dispatch(setSidebarCollapsed(true));
        }
      }

      dispatch(setShowSectionPropertiesPanel(newState));
    });
  }, [showSectionPropertiesPanel, sidebarCollapsed, dispatch]);

  // Section selection handlers
  const handleSectionSelect = useCallback(
    (section: Section | null) => {
      startTransition(() => {
        if (section) {
          // Allow selection of locked sections so users can unlock them
          setSelectedSectionId(section.id);
          dispatch(setSelectedSection(section));

          if (typeof window !== "undefined" && window.innerWidth < 1750) {
            // On smaller screens, ensure only one sidebar is open
            // Close left sidebar when opening section properties panel
            dispatch(setSidebarCollapsed(true));
          }

          dispatch(setShowSectionPropertiesPanel(true));
        } else {
          // Deselect
          handleCloseSectionPanel();
        }
      });
    },
    [handleCloseSectionPanel, dispatch],
  );

  // Add escape key handler to close section panel
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && showSectionPropertiesPanel) {
        handleCloseSectionPanel();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showSectionPropertiesPanel, handleCloseSectionPanel]);

  // Handle preloader exit complete
  const handlePreloaderExitComplete = () => {
    setShowPreloader(false);
    setShowMainContent(true);
    setShowCanvas(true);
  };

  return (
    <>
      {/* Show preloader during initial loading */}
      {showPreloader && (
        <WebBuilderPreloader
          message={currentStep}
          progress={progress}
          showProgress={true}
          isExiting={isPreloaderExiting}
          onExitComplete={handlePreloaderExitComplete}
        />
      )}

      {/* Show resolution warning overlay */}
      {!showPreloader && showWarning && (
        <ResolutionWarningOverlay
          currentWidth={currentWidth}
          minWidth={minWidth}
          onOverride={override}
        />
      )}

      {!showPreloader && (
        <div
          className={`h-screen w-full bg-background dark:bg-auth-background text-foreground flex overflow-hidden transition-all duration-500 ease-out ${
            showMainContent
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-4"
          }`}
        >
          {/* Left Sidebar */}
          <Sidebar
            sidebarCollapsed={sidebarCollapsed}
            setSidebarCollapsed={handleLeftSidebarToggle}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            showMainContent={showMainContent}
            pageId={selectedPageId}
            currentPage={currentPage}
            onDeletePage={handleDeletePage}
          />

          <div
            className={`flex-1 min-w-0 flex flex-col grainy-bg transition-all duration-400 ease-out delay-150 ${
              showMainContent
                ? "opacity-100 translate-x-0"
                : "opacity-0 translate-x-4"
            }`}
            ref={containerRef}
          >
            <div
              className={`transition-all duration-400 ease-out delay-225 z-50 ${
                showMainContent
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 -translate-y-2"
              }`}
            >
              <TopBar
                isDark={isDark}
                onToggleTheme={toggleTheme}
                currentDevice={currentDevice}
                showDeviceSelector={!showFullscreenEditor}
                onDeviceChange={(d) =>
                  startTransition(() => {
                    dispatch(setCurrentDevice(d));
                  })
                }
                onTriggerCanvasShimmer={triggerCanvasShimmer}
                pages={pages}
                isLoadingPages={pagesLoading}
                selectedPageId={selectedPageId}
                currentPage={currentPage}
                sections={sections}
                onPageChange={handlePageChange}
                onOpenAddPage={() => setAddPageModalOpen(true)}
                onPreview={handlePreview}
                onSave={handleSaveAsDraft}
                onShowKeyboardShortcuts={handleShowKeyboardShortcuts}
                onPublish={handlePublish}
                onPublishAll={handlePublishAll}
                isPagePublished={isPagePublished}
                hasUnpublishedChanges={hasUnpublishedChanges}
              />
            </div>

            <div className="flex-1 min-h-0 min-w-0 flex">
              <div
                className={`min-h-0 ${
                  showFullscreenEditor
                    ? "w-[423px] shrink-0 px-4 pb-4"
                    : "flex-1 min-w-0"
                }`}
              >
                {showFullscreenEditor ? (
                  <div className="h-full rounded-2xl border border-border dark:border-auth-background overflow-hidden bg-card dark:bg-auth-card">
                    <Canvas
                      deviceWidth={375}
                      isCanvasShimmering={isCanvasShimmering}
                      currentDevice={currentDevice}
                      pages={pages}
                      pagesLoading={pagesLoading}
                      pagesError={pagesError}
                      currentPage={currentPage}
                      currentPageLoading={currentPageLoading}
                      currentPageError={currentPageError}
                      onRetryFetch={handleRetryFetch}
                      onCreatePage={handleCreatePage}
                      selectedSectionId={selectedSectionId}
                      onSectionSelect={handleSectionSelect}
                      handleCloseSectionPanel={handleCloseSectionPanel}
                      storeId={storeId as string}
                      fullscreenPreview={true}
                    />
                  </div>
                ) : (
                  <Canvas
                    deviceWidth={deviceWidth}
                    isCanvasShimmering={isCanvasShimmering}
                    currentDevice={currentDevice}
                    pages={pages}
                    pagesLoading={pagesLoading}
                    pagesError={pagesError}
                    currentPage={currentPage}
                    currentPageLoading={currentPageLoading}
                    currentPageError={currentPageError}
                    onRetryFetch={handleRetryFetch}
                    onCreatePage={handleCreatePage}
                    selectedSectionId={selectedSectionId}
                    onSectionSelect={handleSectionSelect}
                    handleCloseSectionPanel={handleCloseSectionPanel}
                    storeId={storeId as string}
                  />
                )}
              </div>

              <SectionPropertiesPanel
                isOpen={
                  showFullscreenEditor ? true : showSectionPropertiesPanel
                }
                onToggle={handleRightPanelToggle}
                showMainContent={showMainContent}
                onDebugToggle={() => setIsDebugPanelOpen(true)}
                selectedSectionOverride={selectedSection}
                componentDefinitions={componentDefinitions}
                isFullscreen={showFullscreenEditor}
                showDeviceTabs={!showFullscreenEditor}
              />
            </div>
          </div>
        </div>
      )}

      <WebBuilderAddPageModal
        open={addPageModalOpen}
        onOpenChange={setAddPageModalOpen}
        onFinishedImport={() => actions.fetchPages(1, 20)}
      />

      {/* Debug Panel - Rendered at top level to avoid transform issues */}
      <DebugJsonPanel
        isOpen={isDebugPanelOpen}
        onClose={() => setIsDebugPanelOpen(false)}
        section={selectedSection}
        history={selectedSection ? selectedSectionHistory : []}
        componentDefinitions={componentDefinitions}
      />

      {/* Delete Page Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Page
            </DialogTitle>
            <DialogDescription className="space-y-2">
              {pages.length <= 1 ? (
                <>
                  <p className="text-amber-600 dark:text-amber-400 font-medium">
                    ⚠️ This is your last page!
                  </p>
                  <p>
                    Deleting this page will remove all content and redirect you
                    to import page to start fresh. This action cannot be undone.
                  </p>
                </>
              ) : (
                <p>
                  Are you sure you want to delete this page? This action cannot
                  be undone.
                </p>
              )}
              {pageToDelete && (
                <p className="text-sm">
                  Type{" "}
                  <span className="font-semibold">
                    {pages.find((p: any) => p.id === pageToDelete)?.name}
                  </span>{" "}
                  to confirm deletion:
                </p>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Type page name to confirm"
              value={confirmationInput}
              onChange={(e) => setConfirmationInput(e.target.value)}
              className="w-full"
            />
          </div>
          <DialogFooter className="flex flex-row justify-end gap-3">
            <ButtonOutline onClick={cancelDeletePage} className="min-w-24 h-10">
              Cancel
            </ButtonOutline>
            <ButtonPrimary
              onClick={confirmDeletePage}
              disabled={
                !pageToDelete ||
                confirmationInput.trim() !==
                  pages.find((p: any) => p.id === pageToDelete)?.name?.trim()
              }
              className="bg-destructive hover:bg-destructive/90 text-white disabled:opacity-50 disabled:cursor-not-allowed min-w-36 h-10"
            >
              {pages.length <= 1 ? "Delete & Start Fresh" : "Delete Page"}
            </ButtonPrimary>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Keyboard Shortcuts Panel */}
      <KeyboardShortcutsPanel
        isOpen={isKeyboardShortcutsPanelOpen}
        onClose={() => setIsKeyboardShortcutsPanelOpen(false)}
      />
    </>
  );
}
